import { useState } from "react";
import { useQueryCache } from "react-query";
import { Button, Table, Tag, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import useGetAggregatorUsers, {
  IAggregatorUser,
} from "./hooks/useGetAggregatorUsers";
import useGetAggregatorRoles from "./hooks/useGetAggregatorRoles";
import EditAggregatorUserRoleModal from "./components/EditAggregatorUserRoleModal";

interface IUserRoleMain {
  aggregator_id: number;
}

const UserRoleMain = ({ aggregator_id }: IUserRoleMain) => {
  const [userToEditRole, setUserToEditRole] = useState<IAggregatorUser>();

  const queryCache = useQueryCache();

  const {
    data: usersResponse,
    status,
    isLoading,
  } = useGetAggregatorUsers({ aggregatorId: aggregator_id });
  const { data: rolesResponse, isLoading: isRolesLoading } =
    useGetAggregatorRoles();

  const users = usersResponse?.data ?? [];
  const roles = rolesResponse?.data ?? [];

  const refreshUsers = () => {
    queryCache.invalidateQueries([
      { aggregatorId: aggregator_id },
      "getAggregatorUsers",
    ]);
  };

  const getRowMenuItems = (): MenuProps["items"] => [
    { key: "edit_role", label: "Edit Role(s)" },
  ];

  const handleRowMenuClick =
    (user: IAggregatorUser) =>
    ({ key }: { key: string }) => {
      if (key === "edit_role") {
        setUserToEditRole(user);
      }
    };

  const columns = [
    {
      title: "Name",
      key: "name",
      align: "center" as const,
      render: (_: unknown, user: IAggregatorUser) =>
        `${user.firstName} ${user.lastName}`,
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      align: "center" as const,
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      align: "center" as const,
    },
    {
      title: "Assigned Role(s)",
      key: "roles",
      align: "center" as const,
      render: (_: unknown, user: IAggregatorUser) => (
        <>{user.roles?.map((role) => <Tag key={role}>{role}</Tag>)}</>
      ),
    },
    {
      title: "Status",
      key: "isActive",
      align: "center" as const,
      render: (_: unknown, user: IAggregatorUser) => (
        <Tag color={user.isActive ? "green" : "red"}>
          {user.isActive ? "Active" : "Disabled"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center" as const,
      render: (_: unknown, user: IAggregatorUser) => (
        <Dropdown
          menu={{
            items: getRowMenuItems(),
            onClick: handleRowMenuClick(user),
          }}
          trigger={["click"]}
        >
          <Tooltip title="Manage user">
            <Button type="text" icon={<MoreOutlined />} />
          </Tooltip>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="mt-4 grid__padding">
      <Table
        rowKey="userId"
        columns={columns}
        dataSource={users}
        loading={isLoading}
        locale={{
          emptyText:
            status === "error"
              ? "Unable to fetch users for this aggregator."
              : "There are currently no users",
        }}
      />

      <EditAggregatorUserRoleModal
        isOpen={!!userToEditRole}
        onClose={() => setUserToEditRole(undefined)}
        user={userToEditRole}
        roles={roles}
        rolesLoading={isRolesLoading}
        aggregatorId={aggregator_id}
        onSuccess={refreshUsers}
      />
    </div>
  );
};

export default UserRoleMain;
